import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Spinner,
  Alert,
  Badge,
  Input,
  Label,
  FormGroup
} from 'reactstrap';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';

const SendAnnexureModal = ({ isOpen, toggle, onEmailsSent }) => {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchShortlistedApplications();
      setSelectedApplications([]);
      setSearchTerm('');
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredApplications(applications);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = applications.filter(application => {
        const idMatch = application.id.toString().includes(searchLower);
        const nameMatch = application.name.toLowerCase().includes(searchLower);
        const emailMatch = application.email.toLowerCase().includes(searchLower);
        const programMatch = application.program && application.program.toLowerCase().includes(searchLower);
        return idMatch || nameMatch || emailMatch || programMatch;
      });
      setFilteredApplications(filtered);
    }
  }, [searchTerm, applications]);

  const fetchShortlistedApplications = async () => {
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/admin/dashboard/applications`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: 1,
            pageSize: 1000, // Get all shortlisted applications
            filters: JSON.stringify({ reviewStatus: ['Shortlisted'] })
          }
        }
      );

      if (response.data.success) {
        // Filter out applications that already have status 'Sent for Annexure'
        const apps = response.data.data.formattedApplications || [];
        const filteredApps = apps.filter(
          app => app.reviewStatus === 'Shortlisted' && app.status !== 'Sent for Annexure'
        );
        setApplications(filteredApps);
        setFilteredApplications(filteredApps);
        
        if (filteredApps.length === 0) {
          setError('No shortlisted applications available. All shortlisted candidates have already been sent annexure emails.');
        }
      }
    } catch (err) {
      console.error('Error fetching shortlisted applications:', err);
      setError(err.response?.data?.message || 'Failed to load shortlisted applications');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectApplication = (applicationId) => {
    setSelectedApplications(prev => {
      if (prev.includes(applicationId)) {
        return prev.filter(id => id !== applicationId);
      } else {
        return [...prev, applicationId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedApplications.length === filteredApplications.length) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(filteredApplications.map(a => a.id));
    }
  };

  const handleSendEmails = async () => {
    if (selectedApplications.length === 0) {
      setError('Please select at least one application');
      return;
    }

    const confirmMessage = `Are you sure you want to send annexure emails to ${selectedApplications.length} candidate(s)? Their status will be updated to "Sent for Annexure".`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/admin/dashboard/send-annexure-emails`,
        {
          applicationIds: selectedApplications
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const { processed, emailResults } = response.data.data;
        
        let successMsg = `Successfully sent annexure emails to ${processed} candidate(s).`;

        // Show email results
        if (emailResults && emailResults.length > 0) {
          const failedEmails = emailResults.filter(e => e.status === 'failed');
          if (failedEmails.length > 0) {
            successMsg += ` Warning: ${failedEmails.length} email(s) failed to send.`;
          }
        }

        setSuccess(successMsg);
        setSelectedApplications([]);
        
        // Refresh the list
        setTimeout(() => {
          fetchShortlistedApplications();
          if (onEmailsSent) {
            onEmailsSent();
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Error sending annexure emails:', err);
      setError(err.response?.data?.message || 'Failed to send annexure emails');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>
        Send Annexure Email to Shortlisted Candidates
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {success && <Alert color="success">{success}</Alert>}

        <div className="mb-3">
          <Alert color="info">
            <strong>Send Annexure Request:</strong> This action will send an email to selected shortlisted candidates 
            requesting them to submit additional documents (annexure). Their application status will be updated to "Sent for Annexure".
          </Alert>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading shortlisted applications...</p>
          </div>
        ) : applications.length === 0 ? (
          <Alert color="warning">
            No shortlisted applications available for sending annexure emails.
          </Alert>
        ) : (
          <>
            <div className="mb-3">
              <Input
                type="text"
                placeholder="Search by ID, Name, Email, or Program..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={processing}
              />
            </div>

            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div>
                <strong>{filteredApplications.length}</strong> shortlisted application(s) found
                {selectedApplications.length > 0 && (
                  <span className="ms-3">
                    <Badge color="primary">{selectedApplications.length} selected</Badge>
                  </span>
                )}
              </div>
              <FormGroup check>
                <Input
                  type="checkbox"
                  checked={selectedApplications.length === filteredApplications.length && filteredApplications.length > 0}
                  onChange={handleSelectAll}
                  disabled={processing}
                />
                <Label check>Select All</Label>
              </FormGroup>
            </div>

            {filteredApplications.length === 0 ? (
              <Alert color="info" className="text-center">
                No applications found matching your search criteria.
              </Alert>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table striped bordered hover responsive>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '50px' }}>
                        <Input
                          type="checkbox"
                          checked={selectedApplications.length === filteredApplications.length && filteredApplications.length > 0}
                          onChange={handleSelectAll}
                          disabled={processing}
                        />
                      </th>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Program</th>
                      <th>Status</th>
                      <th>Review Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((application) => (
                    <tr key={application.id}>
                      <td>
                        <Input
                          type="checkbox"
                          checked={selectedApplications.includes(application.id)}
                          onChange={() => handleSelectApplication(application.id)}
                          disabled={processing}
                        />
                      </td>
                      <td>{application.id}</td>
                      <td>{application.name}</td>
                      <td style={{ fontSize: '0.85rem' }}>{application.email}</td>
                      <td style={{ fontSize: '0.85rem' }}>{application.program}</td>
                      <td>
                        <Badge color="primary">{application.status}</Badge>
                      </td>
                      <td>
                        <Badge color="success">{application.reviewStatus}</Badge>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="info"
          onClick={handleSendEmails}
          disabled={processing || selectedApplications.length === 0 || applications.length === 0}
        >
          {processing ? <Spinner size="sm" /> : null}
          {' '}Send Annexure Email ({selectedApplications.length})
        </Button>
        <Button color="secondary" onClick={toggle} disabled={processing}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SendAnnexureModal;
